// The Plan room's menus over items: a card's or row's context menu (the Mac's binder and card menus with
// StatusLabelMenus) — the structure menu where the arrangement can be restructured, only Status and Label in a
// collection (documents there can be edited and have their status changed, never moved) — and Structure ▸ Next/Previous
// Status (⌃⌘] / ⌃⌘[; Ctrl+Alt+] / Ctrl+Alt+[ elsewhere), which steps the selected document through the workflow.
import type { MetadataValue } from '@writing-suite/core';
import { announce } from '../../shell/context.ts';
import type { Workspace } from '../../shell/workspace.ts';
import { planEdit } from '../../store/plan.ts';
import { choiceEntries } from '../FieldEditor.tsx';
import { itemMenu } from '../itemMenu.ts';
import type { MenuEntry } from '../Menu.tsx';
import { fragmentsOf, locate } from '../structure.ts';

/** Status and Label for the fragments of these nodes (one change each), from the first's values. */
export function statusLabelEntries(w: Workspace, nodeIds: string[]): MenuEntry[] {
  const ids = fragmentsOf(w.tree, nodeIds);
  const first = ids[0];
  if (!first || !w.live) return [];
  const live = w.live;
  const out: MenuEntry[] = [];
  const fields = w.project.fields();
  const entities = w.project.entities();
  for (const id of ['status', 'label']) {
    const field = fields.find((f) => f.id === id);
    if (!field || !field.options.length) continue;
    const value: MetadataValue | undefined = w.project.fragment(first)?.metadata[id];
    if (out.length) out.push({ kind: 'separator' });
    out.push({ kind: 'heading', label: field.name });
    out.push(...choiceEntries(field, entities, value, (v) => planEdit(live, (e) => e.setMetadataOn(ids, id, v))));
  }
  return out;
}

/** A card's or a contents row's menu. */
export function planItemMenu(w: Workspace, nodeIds: string[], o: { onRename?: (nodeId: string) => void; onCreated?: (nodeId: string) => void }): MenuEntry[] {
  if (w.canStructure) return itemMenu(w, nodeIds, o);
  if (w.canWrite && w.arrangement.role === 'collection') {
    const entries = statusLabelEntries(w, nodeIds);
    return entries.length ? entries : [{ kind: 'heading', label: 'In a collection' }];
  }
  return itemMenu(w, nodeIds, o);
}

/** The document Next/Previous Status acts on: the caret's in Write, else the page's item. */
export function selectedFragment(w: Pick<Workspace, 'caretFragment' | 'node' | 'tree'>, room: string | null): string | null {
  if (room === 'write' && w.caretFragment) return w.caretFragment;
  if (w.node?.fragmentId) return w.node.fragmentId;
  return null;
}

/** Next Status (or Previous): the selected document one step through the workflow; says where it went. */
export function stepStatus(w: Workspace, forward: boolean, room: string | null): boolean {
  if (!w.live || !w.canWrite) return false;
  const id = selectedFragment(w, room);
  if (!id) {
    announce('Choose a document first.');
    return false;
  }
  const live = w.live;
  const moved = planEdit(live, (e) => e.stepStatus(id, forward));
  if (!moved) {
    announce(forward ? 'That’s the last status.' : 'That’s the first status.');
    return false;
  }
  const name = w.project.fields().find((f) => f.id === 'status')?.options.find((o) => o.id === moved.status)?.name ?? moved.status;
  announce(`Status: ${name}`);
  return true;
}

/** ⌃⌘] / ⌃⌘[ on a Mac (Control and Command together), Ctrl+Alt+] / Ctrl+Alt+[ elsewhere; never with Shift. */
export function statusKey(e: Pick<KeyboardEvent, 'metaKey' | 'ctrlKey' | 'altKey' | 'shiftKey' | 'code' | 'key'>): 'next' | 'previous' | null {
  if (!e.ctrlKey || !(e.metaKey || e.altKey) || e.shiftKey) return null;
  if (e.code === 'BracketRight' || e.key === ']') return 'next';
  if (e.code === 'BracketLeft' || e.key === '[') return 'previous';
  return null;
}

export const titleOf = (w: Pick<Workspace, 'tree'>, nodeId: string) => locate(w.tree, nodeId)?.node.title || 'Untitled';
